import { useTranslation } from 'react-i18next'

import { sleep } from '../../../../utils/async'
import { isDocFlowWidgetStatusCompleted } from '../../DocFlow.utils.widgets'
import { DocFlowMappingProcessCell } from '../../DocFlowMapping'
import { getMappingValueInArea } from '../../DocFlowUploading'
import {
  DOC_FLOW_MAPPING_WIDGET_MAPPING_CALLBACK_TIMEOUT,
  getDocFlowSplittingAndMappingWidgetPermanentNotifications,
  getWidgetMappingInitialValues,
  getWidgetMappingNotifications,
  getWidgetMappingProcessPayload,
  getWidgetMappingSelectedAreaInfoData,
  getWidgetMappingSelectedAreaPayload,
  isMappingArea,
} from '../../DocFlowWidgets'
import { useDocFlowContext } from '../useDocFlowContext'
import { useDocFlowMappingContext } from '../useDocFlowMappingContext'
import {
  DocFlowMappingWidgetWithSelectedAreaNotificationType,
  useDocFlowMappingWidgetWithSelectedArea,
} from '../useDocFlowMappingWidgetWithSelectedArea'

export function useDocFlowMappingWidgetMapping({ index, widgetName }) {
  const { t } = useTranslation()

  const { handleReloadDocFlow } = useDocFlowContext()

  const {
    handleGetActiveFile,
    handleReloadFileProcess,
    handleReloadUploadingFilesAll,
    isMappedDataLoading,
    isPdfSelectAreaInfoValueLoading,
    isProcessDataLoading,
    processData,
    setMappedDataLoading,
  } = useDocFlowMappingContext()

  const initialNotifications =
    getDocFlowSplittingAndMappingWidgetPermanentNotifications(t)

  const widgetNotifications = getWidgetMappingNotifications(t)

  const {
    disabled,
    handleAddNotification,
    handleChangeAccuracy,
    handleClearFieldType,
    handleClickNext: handleWidgetWithSelectedAreaClickNext,
    handleClickPrevious,
    handleDebounceWidgetGetSelectedAreaInfo,
    handlePrependNotification,
    handleRemoveAdditionalNotifications,
    handleRemoveNotification,
    handleWidgetGetSelectedAreaInfo,
    hasError,
    nextButtonDisabled,
    notifications,
    unvisited,
    widgetDone,
  } = useDocFlowMappingWidgetWithSelectedArea({
    apiFunction: getMappingValueInArea,
    areaType: DocFlowMappingProcessCell.MappingArea,
    getSelectedAreaData: getWidgetMappingSelectedAreaInfoData,
    getSelectedAreaPayload: getWidgetMappingSelectedAreaPayload,
    getSubmitPayload: getWidgetMappingProcessPayload,
    hasAreaCellName: DocFlowMappingProcessCell.HasMappingArea,
    hasErrorCellName: DocFlowMappingProcessCell.HasPreMappingError,
    index,
    initialNotifications,
    isAreaValue: isMappingArea,
    phaseType: DocFlowMappingProcessCell.MappingPhase,
    widgetName,
    widgetNotifications,
    onSuccessSubmitNext: handleSuccessSubmitNext,
  })

  function handleFinishMappedDataLoading() {
    handleRemoveNotification(
      widgetNotifications[
        DocFlowMappingWidgetWithSelectedAreaNotificationType.MappingInProcess
      ].id
    )

    setMappedDataLoading(false)
  }

  async function handleSuccessSubmitNext() {
    setMappedDataLoading(true)

    while (true) {
      try {
        const { data } = await handleGetActiveFile()

        if (isDocFlowWidgetStatusCompleted(data.status)) {
          await handleReloadFileProcess()

          handleFinishMappedDataLoading()

          return data
        }

        handlePrependNotification(
          widgetNotifications[
            DocFlowMappingWidgetWithSelectedAreaNotificationType
              .MappingInProcess
          ]
        )

        await sleep(DOC_FLOW_MAPPING_WIDGET_MAPPING_CALLBACK_TIMEOUT)
      } catch (error) {
        handleFinishMappedDataLoading()

        throw error
      }
    }
  }

  const initialValues = getWidgetMappingInitialValues(processData, t)

  function handleClickNext(values, formikHelers) {
    handleWidgetWithSelectedAreaClickNext(values, formikHelers)?.then(() => {
      handleReloadUploadingFilesAll()
      handleReloadDocFlow()
    })
  }

  return {
    disabled,
    handleAddNotification,
    handleChangeAccuracy,
    handleClearFieldType,
    handleClickNext,
    handleClickPrevious,
    handleDebounceWidgetGetSelectedAreaInfo,
    handleGetMappingSelectedAreaInfo: handleWidgetGetSelectedAreaInfo,
    handlePrependNotification,
    handleRemoveAdditionalNotifications,
    handleRemoveNotification,
    hasError,
    initialValues,
    isLoading:
      isPdfSelectAreaInfoValueLoading ||
      isProcessDataLoading ||
      isMappedDataLoading,
    nextButtonDisabled,
    notifications,
    unvisited,
    widgetDone,
    widgetNotifications,
  }
}
